import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { parseIterationResponse } from '../../services/iterationResponse';

interface Props {
  strategyId?: string;
  onComplete: () => void;
}

type Mode = 'INIT' | 'MANUAL' | 'UPLOAD';

const Module4Truth: React.FC<Props> = ({ strategyId, onComplete }) => {
  const [truthData, setTruthData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [userTruth, setUserTruth] = useState('');
  const [mode, setMode] = useState<Mode>('INIT');
  const [chatInputs, setChatInputs] = useState<{ [key: number]: string }>({});
  const [chatMessages, setChatMessages] = useState<{ [key: number]: {role: string, content: string}[] }>({});
  
  // Track files selected by user
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  useEffect(() => {
    const loadExisting = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/strategies/${strategyId}`);
        const data = res.data;
        if (data && data.module_4_activating_truth) {
          let parsed = data.module_4_activating_truth;
          if (typeof parsed === 'string') {
            try { parsed = JSON.parse(parsed); } catch(e){}
          }
          if (parsed && ((parsed.opciones && parsed.opciones.length > 0) || parsed.verdad_seleccionada)) {
            setTruthData(parsed);
          }
        }
      } catch (err) {
        console.error("Error cargando verdad activadora existente:", err);
      } finally {
        setLoading(false);
      }
    };
    if (strategyId) {
      loadExisting();
    }
  }, [strategyId]);

  const handleIterate = async (idx: number) => {
    const currentInput = chatInputs[idx];
    if (!currentInput) return;
    setLoading(true);
    try {
      setChatInputs(prev => ({ ...prev, [idx]: '' }));
      const contextualizedMessage = `Sobre la opción ${idx + 1}: ${currentInput}`;
      const res = await api.post(`/strategies/${strategyId}/modules/4/chat`, { message: contextualizedMessage });
      const result = parseIterationResponse<any>(res.data);

      if (result.state && Array.isArray(result.state.opciones)) {
        setTruthData((prev: any) => ({ ...(prev || {}), ...result.state }));
      }

      const replyMsg = result.reply || 'He actualizado las opciones según tus indicaciones.';
      setChatMessages(prev => ({
        ...prev,
        [idx]: [...(prev[idx] || []), { role: 'user', content: currentInput }, { role: 'assistant', content: replyMsg }]
      }));
    } catch (err) {
      alert('Error en iteración: ' + err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUploadAndGenerate = async () => {
    if (selectedFiles.length === 0) return;
    
    setUploading(true);
    setLoading(true);
    let combinedText = '';

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const formData = new FormData();
        formData.append('context_files', file);

        const res = await api.post(`/strategies/${strategyId}/modules/4/upload_context`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (res.data.text) {
          combinedText += (combinedText ? '\n\n---\n\n' : '') + res.data.text;
        }
      }

      // Automatically generate truth with the combined text
      const res = await api.post(`/strategies/${strategyId}/modules/4/process`, { user_truth: combinedText });
      let data = res.data;
      if (typeof data.module_4_activating_truth === 'string') {
        setTruthData(JSON.parse(data.module_4_activating_truth));
      } else {
        setTruthData(data.module_4_activating_truth);
      }
    } catch (err) {
      alert('Error al subir y analizar archivos: ' + err);
    } finally {
      setUploading(false);
      setLoading(false);
      setSelectedFiles([]);
    }
  };

  const generateTruth = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/strategies/${strategyId}/modules/4/process`, { user_truth: userTruth });
      let data = res.data;
      if (typeof data.module_4_activating_truth === 'string') {
        setTruthData(JSON.parse(data.module_4_activating_truth));
      } else {
        setTruthData(data.module_4_activating_truth);
      }
    } catch (err) {
      alert('Error: ' + err);
    } finally {
      setLoading(false);
    }
  };

  const handleTextChange = (idx: number, newText: string) => {
    const updated = { ...truthData };
    updated.opciones[idx].justificacion = newText;
    setTruthData(updated);
  };

  const handleOptionNameChange = (idx: number, newName: string) => {
    const updated = { ...truthData };
    updated.opciones[idx].opcion = newName;
    setTruthData(updated);
  };

  const handleExportPDF = async () => {
    try {
      const res = await api.get(`/strategies/${strategyId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Estrategia_Modulo_IV.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error al exportar PDF: ' + err);
    }
  };

  const selectOption = async (idx: number) => {
    setLoading(true);
    try {
      const selectedTruth = truthData.opciones[idx];
      const newState = {
        opciones: [selectedTruth],
        verdad_seleccionada: selectedTruth.opcion
      };
      
      await api.put(`/strategies/${strategyId}/modules/4/update-state`, newState);
      setTruthData(newState);
    } catch (err) {
      alert('Error al guardar selección: ' + err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Módulo IV: La Verdad Activadora</h2>
      <p style={styles.description}>Combinar el driver con la realidad del usuario. Aquí entra la intuición del estratega.</p>

      {truthData && truthData.verdad_seleccionada ? (
        <div style={styles.card}>
          <h3 style={{ ...styles.cardTitle, color: 'var(--success)' }}>Verdad Activadora Seleccionada</h3>
          <p style={{ fontSize: '18px', fontWeight: 600, margin: '8px 0' }}>"{truthData.verdad_seleccionada}"</p>
          {truthData.opciones && truthData.opciones[0] && (
            <p style={{ ...styles.cardContent, lineHeight: 1.6, color: 'var(--text-primary)' }}>{truthData.opciones[0].justificacion}</p>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
            <button style={{ ...styles.secondaryButton, color: 'var(--accent)', borderColor: 'var(--accent)' }} onClick={handleExportPDF}>
              Exportar PDF
            </button>
            <button style={styles.successButton} onClick={async () => {
              setLoading(true);
              try {
                const selectedTruth = (truthData.opciones && truthData.opciones[0]) || {};
                const newState = {
                  opciones: [selectedTruth],
                  verdad_seleccionada: truthData.verdad_seleccionada
                };
                await api.put(`/strategies/${strategyId}/modules/4/update-state`, newState);
                onComplete();
              } catch (e) {
                console.error("Error al avanzar al siguiente módulo:", e);
                alert('No se pudo guardar la verdad activadora. Inténtalo de nuevo.');
              } finally {
                setLoading(false);
              }
            }}>Siguiente Módulo</button>
          </div>
        </div>
      ) : !truthData ? (
        <>
          {mode === 'INIT' && (
            <div style={styles.card}>
              <h3>¿Tienes ya la verdad activadora?</h3>
              <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
                <button style={styles.primaryButton} onClick={() => setMode('MANUAL')}>Sí la tengo</button>
                <button style={styles.secondaryButton} onClick={() => setMode('UPLOAD')}>No la tengo</button>
              </div>
            </div>
          )}

          {mode === 'MANUAL' && (
            <div style={styles.card}>
              <h3>Redacta tu intuición o verdad de calle:</h3>
              <textarea 
                style={styles.textarea} 
                value={userTruth} 
                onChange={e => setUserTruth(e.target.value)} 
                placeholder="¿Qué intuición o verdad de calle quieres redactar?" 
                rows={4}
                disabled={loading}
              />
              <button style={{ ...styles.primaryButton, marginTop: '16px' }} onClick={generateTruth} disabled={loading || !userTruth}>
                {loading ? 'Generando Opciones...' : 'Sintetizar Verdad Activadora'}
              </button>
            </div>
          )}

          {mode === 'UPLOAD' && (
            <div style={styles.card}>
              <h3>Sube los materiales de contexto (Imágenes, Videos o Documentos):</h3>
              <input 
                type="file" 
                multiple 
                style={styles.fileInput} 
                accept=".pdf,.doc,.docx,.txt,image/*,video/*" 
                onChange={e => {
                  if (e.target.files) {
                    setSelectedFiles(Array.from(e.target.files));
                  }
                }} 
                disabled={loading || uploading} 
              />
              {selectedFiles.length > 0 && (
                <div style={{ marginBottom: '16px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                  <strong>Archivos seleccionados:</strong>
                  <ul>
                    {selectedFiles.map((file, i) => <li key={i}>{file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)</li>)}
                  </ul>
                </div>
              )}
              {uploading && (
                <p style={{ color: 'var(--accent)', fontWeight: 600, fontSize: '14px', margin: '8px 0' }}>Procesando y analizando archivos multimedia (puede tardar hasta 60s)...</p>
              )}
              <button 
                style={{ ...styles.primaryButton, marginTop: '16px' }} 
                onClick={handleFileUploadAndGenerate} 
                disabled={loading || uploading || selectedFiles.length === 0}
              >
                {loading ? 'Subiendo y Analizando...' : 'Subir archivos y Generar Verdad'}
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {truthData.opciones && truthData.opciones.map((opcion: any, idx: number) => (
              <div key={idx} style={{ ...styles.card, background: 'var(--bg-hover)', color: 'var(--text-primary)', textAlign: 'left', padding: '24px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Opción {idx + 1}</label>
                <input
                  type="text"
                  style={{ ...styles.chatInput, width: '100%', marginBottom: '12px', fontWeight: 'bold', padding: '12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'transparent', color: 'var(--text-primary)' }}
                  value={opcion.opcion || ''}
                  onChange={(e) => handleOptionNameChange(idx, e.target.value)}
                  placeholder="Enunciado de la verdad..."
                />
                
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Justificación</label>
                <textarea
                  style={{ ...styles.textarea, width: '100%', resize: 'vertical' }}
                  rows={4}
                  value={opcion.justificacion}
                  onChange={(e) => handleTextChange(idx, e.target.value)}
                />

                {/* Chat de Iteración Específico por Opción */}
                <div style={{ marginTop: '24px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                  <h4 style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '12px', fontWeight: 600 }}>Iterar vía chat ("¿qué te hace ruido?")</h4>
                  
                  {(chatMessages[idx] && chatMessages[idx].length > 0) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px', maxHeight: '200px', overflowY: 'auto' }}>
                      {chatMessages[idx].map((msg, i) => (
                        <div key={i} style={{ 
                          alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                          background: msg.role === 'user' ? 'var(--accent)' : 'var(--bg-card)',
                          color: msg.role === 'user' ? '#fff' : 'var(--text-primary)',
                          padding: '10px 14px',
                          borderRadius: '8px',
                          maxWidth: '80%',
                          border: msg.role === 'assistant' ? '1px solid var(--border)' : 'none'
                        }}>
                          <p style={{ margin: 0, fontSize: '13px' }}>{msg.content}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <input 
                      type="text" 
                      style={{ flex: 1, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'transparent', color: 'var(--text-primary)', fontFamily: 'inherit', fontSize: '13px' }} 
                      value={chatInputs[idx] || ''} 
                      onChange={e => setChatInputs(prev => ({...prev, [idx]: e.target.value}))} 
                      placeholder="Escribe tus comentarios aquí..." 
                      onKeyDown={e => e.key === 'Enter' && handleIterate(idx)}
                      disabled={loading}
                    />
                    <button style={{ ...styles.primaryButton, padding: '0 20px' }} onClick={() => handleIterate(idx)} disabled={loading || !chatInputs[idx]}>
                      {loading ? '...' : 'Enviar'}
                    </button>
                  </div>
                </div>
                
                <button style={{...styles.successButton, alignSelf: 'flex-start', marginTop: '16px'}} onClick={() => selectOption(idx)} disabled={loading}>
                  {loading ? 'Guardando...' : 'Elegir esta opción'}
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', flexDirection: 'column', gap: '16px' },
  title: { fontSize: '24px', color: 'var(--text-primary)', marginBottom: '8px' },
  description: { color: 'var(--text-secondary)', marginBottom: '24px' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' },
  label: { fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' },
  textarea: { padding: '12px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '14px', fontFamily: 'inherit', background: 'transparent', color: 'var(--text-primary)' },
  fileInput: { display: 'block', padding: '8px', color: 'var(--text-primary)', marginBottom: '8px' },
  primaryButton: { background: 'var(--accent)', color: '#fff', padding: '12px 24px', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start' },
  secondaryButton: { background: 'var(--bg-card)', color: 'var(--text-primary)', padding: '12px 24px', border: '1px solid var(--border)', borderRadius: '4px', fontWeight: 600, cursor: 'pointer' },
  successButton: { background: 'var(--success)', color: '#fff', padding: '12px 24px', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-end', marginTop: '24px' },
  card: { padding: '32px', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--bg-card)' },
  cardTitle: { fontSize: '14px', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '16px', letterSpacing: '0.05em' },
  cardContent: { margin: 0, lineHeight: 1.4, color: 'var(--text-primary)' }
};

export default Module4Truth;
